import Skeleton, { SkeletonTheme } from 'react-loading-skeleton'
import { EnrollmentMultiLayout } from '@egw/layouts'
import { Input, Button, Alert, AlertType, GenericError } from '@egw/components'
import { Logger } from '@egw/logger'
import { WEALTH_ROUTES, EGM_A_COOKIE } from '@egw/constants'
import { useEffect, useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useGetEnrollmentMultiReturnEnterPhoneData, useSubmitEnrollmentMultiReturnEnterPhoneData } from '@egw/api'
import { useRouter } from '@egw/router'
import { useTranslation } from '@egw/i18n'
import { Cookies } from '@egw/clients'
import { enrollmentCookieService } from '@egw/enrollment-cookie'
import { formatPhoneNumberWwithMask, preformatPhoneNumber } from '../../../../utils/phone-number/phone-number'
import { PageHeader } from '../../../../components'
import { ALL_ENROLLMENT_ROUTES } from '../../../../constants'

const log = new Logger('Enrollment - Multi - ReturnEnterPhone')

interface FormData {
  phoneNumber: string
}

export const ReturnEnterPhonePage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { navigate } = useRouter()
  const [showError, setShowError] = useState(false)

  const { data, isLoading, error } = useGetEnrollmentMultiReturnEnterPhoneData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentMultiReturnEnterPhoneData()

  // Set enrollment session cookie if it doesn't exist
  useEffect(() => {
    const existingCookie = Cookies.get(EGM_A_COOKIE)
    if (!existingCookie) {
      void enrollmentCookieService.resetSession()
    }
  }, [])

  useEffect(() => {
    if (data?.error === 'SESSION_NOT_FOUND') {
      navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.RETURN_SIGN_IN.path}`)
    }
  }, [data?.error, navigate])

  const {
    control,
    handleSubmit,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm({
    defaultValues: {
      phoneNumber: '',
    },
    mode: 'onChange',
  })

  const variant = 'otp' // serve otp here only

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      setShowError(false)
      // Get payload
      const payload = {
        phone: {
          phoneNumber: preformatPhoneNumber(formData.phoneNumber),
          countryPhoneCode: '1',
        },
      }

      try {
        const response = await mutate(payload)
        // Handle response
        if (response?.nextStep === 'VERIFY_PHONE') {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.RETURN_VERIFY_PHONE.path}`)
        } else if (response?.error === 'SESSION_NOT_FOUND') {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.RETURN_SIGN_IN.path}`)
        } else if (response?.error === 'INVALID_INPUT_COMBINATION') {
          navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ALL_ENROLLMENT_ROUTES.MULTI.ROUTES.RETURN_SIGN_IN.path}`)
        } else if (response?.error === 'REENTRY_INELIGIBLE') {
          setShowError(true)
        }
      } catch (e) {
        log.error('Error submitting form', e)
      }
    }
  })

  return (
    <EnrollmentMultiLayout title={t(`multi.return.enter_phone.${variant}.page_title`)}>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? (
        <div className="w-full">
          <SkeletonTheme baseColor="#eeefee" highlightColor="rgba(255, 255, 255, 0.5)">
            <Skeleton count={1} height="100px" className="mb-10" />
            <Skeleton count={5} height="50px" />
          </SkeletonTheme>
        </div>
      ) : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
          <PageHeader title={t(`multi.return.enter_phone.${variant}.title`)} className="mb-md" />
          <p className="typo-primary-base-regular text-text-meta mb-xl">
            {t(`multi.return.enter_phone.${variant}.description`)}
          </p>

          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: t('common.required_field'),
              pattern: {
                value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/i,
                message: t(`multi.return.enter_phone.${variant}.invalid_phone_number`),
              },
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t(`multi.return.enter_phone.${variant}.phone_number`)}
                onInput={(e) => {
                  formatPhoneNumberWwithMask(e)
                }}
                required
                testId="phoneNumber_input"
                error={touchedFields.phoneNumber ? formErrors.phoneNumber?.message : undefined}
              />
            )}
          />

          <div className="flex-1 md:hidden" />

          {showError ? <Alert alertType={AlertType.Error} description={t('multi.return.sign_in.error')} className="mt-sm" /> : null}

          {/* AUTO GENERATED COMPONENTS */}

          <Button
            type="submit"
            className="w-full md:w-min mt-xl"
            isDisabled={!isValid}
            isLoading={isSubmitting}
            testId="primaryAction_button"
          >
            {t('common.continue')}
          </Button>
        </form>
      ) : null}

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentMultiLayout>
  )
}
